import { createContext, useContext, useMemo, useState } from 'react';
import { login as loginService, logout as logoutService } from '../services/auth';

const AuthContext = createContext(null);

const STORAGE_KEY    = 'auth_user';
const VIEW_MODE_KEY  = 'ipms_view_mode';

const getInitialUser = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try { return JSON.parse(stored); }
  catch { localStorage.removeItem(STORAGE_KEY); return null; }
};

// viewMode: '' = Toate, 'alienzaz' = Cabinet/Alienzaz, 'inventa' = Inventa
const getInitialViewMode = () => {
  const stored = localStorage.getItem(VIEW_MODE_KEY);
  if (stored !== null) return stored;
  const u = getInitialUser();
  return u?.societate || '';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getInitialUser);
  const [viewMode, setViewModeState] = useState(getInitialViewMode);

  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const loginUser = async (username, password) => {
    const userData = await loginService(username, password);
    setUser(userData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    // Initialize view mode from user's societate on login
    const mode = userData.societate || '';
    setViewModeState(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
    return userData;
  };

  const logoutUser = async () => {
    await logoutService();
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    setViewModeState('');
    localStorage.removeItem(VIEW_MODE_KEY);
  };

  const updateUser = (nextUser) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
      return;
    }
    localStorage.removeItem(STORAGE_KEY);
  };

  const value = useMemo(
    () => ({ user, loginUser, logoutUser, updateUser, viewMode, setViewMode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, viewMode]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
